import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useAuthModal } from '../AuthModalContext';
import { Court, ROLE_META, ROLE_ORDER } from './DropInCourt';
import HamburgerMenu from './HamburgerMenu';
import { useCart } from '../CartContext';
import './TournamentFlow.css';
import BlitzInfoModal from './BlitzInfoModal';
import OvrCupInfoModal, { CUP_INFO } from './OvrCupInfoModal';
import InfoIcon from './InfoIcon';

const MIN_ROSTER = 6;
const MAX_ROSTER = 8;
const MIN_WOMEN  = 2;

// Static display content per tournament (name, desc, format rules stay in code;
// pricing, open/closed, and event_date come from the `seasons` DB table).
// Early bird discounts — deadline is end of day in Eastern time (UTC-4)
const EARLY_BIRD = {
  szn6: { deadline: new Date('2026-06-19T04:00:00Z'), discountCents: 2000 }, // $20 off, expires Jun 18 midnight ET
};
function isEarlyBirdActive(key) {
  const eb = EARLY_BIRD[key];
  return !!(eb && Date.now() < eb.deadline.getTime());
}

// Tournaments that are full — card shows SOLD OUT and can't be selected.
const SOLD_OUT = { szn6: true };

const TOURN_META = {
  blitz: {
    name: 'OVR BLITZ',
    fee: '$25/player',
    faFee: '$25',
    perPlayerCents: 2500,
    format: '4 Teams · 2 Courts · 4-Hour Block',
    desc: 'Fast-paced mini tournament. Guaranteed games, playoffs, winner photos, and competitive OVR energy.',
  },
  szn6: {
    name: 'OVR CUP SZN VI',
    fee: '$40/player',
    faFee: '$40',
    perPlayerCents: 4000,
    format: 'Full Day · Multi-Court · Norm Fenn Gymnasium · Carleton University',
    desc: 'Full-day cup tournament. Bracket play, awards ceremony.',
  },
  carleton: {
    name: 'OVR × CARLETON FUNDRAISER',
    date: 'SUN OCT 25 · 1–9 PM',
    fee: '$40/player',
    faFee: '$40',
    perPlayerCents: 4000,
    format: '10 Teams · 2 Pools · Norm Fenn Gymnasium · Carleton University',
    desc: 'Co-ed cup tournament fundraiser with Carleton. Pool play, full playoff bracket, prizes for the winners.',
  },
  szn7: {
    name: 'OVR CUP SZN VII',
    fee: '$45/player',
    faFee: '$35',
    perPlayerCents: 4500,
    format: '10 Teams · 2 Courts · 8-Hour Block',
    desc: 'Full-day cup tournament. Bracket play, awards ceremony.',
  },
};

function emptyPlayer() { return { firstName: '', lastName: '', gender: '', position: '' }; }
function fullName(p)   { return `${p.firstName || ''} ${p.lastName || ''}`.trim(); }
function namedCount(players) { return players.filter(p => fullName(p)).length; }
function womenCount(players) { return players.filter(p => fullName(p) && p.gender === 'F').length; }
function faClaims(pos) {
  const c = { S: [], OH: [], MB: [], OPP: [], L: [] };
  if (pos && c[pos] !== undefined) c[pos] = [{ isYou: true }];
  return c;
}

const TEAM_STEPS = ['tourn', 'type', 'team_info', 'roster', 'confirm', 'done'];
const FA_STEPS   = ['tourn', 'type', 'fa_info',   'fa_pos', 'confirm', 'done'];
const STEP_LABELS = {
  tourn: 'TOURNAMENT', type: 'TYPE', team_info: 'TEAM INFO',
  roster: 'ROSTER', fa_info: 'YOUR INFO', fa_pos: 'POSITION',
  confirm: 'CONFIRM', done: 'DONE',
};

function StepBar({ step, regType }) {
  const steps = regType === 'team' ? TEAM_STEPS : regType === 'fa' ? FA_STEPS : ['tourn', 'type'];
  const idx = Math.max(steps.indexOf(step), 0);
  return (
    <div className="tf-step-bar-wrap">
      <div className="tf-step-bar">
        {steps.map((_, i) => (
          <div key={i} className={`tf-pill ${i < idx ? 'done' : i === idx ? 'active' : ''}`} />
        ))}
      </div>
      <div className="tf-step-meta">
        <span>STEP {idx + 1} / {steps.length}</span>
        <span>{STEP_LABELS[step] || ''}</span>
      </div>
    </div>
  );
}

function Modal({ onClose, children }) {
  return (
    <div className="tf-modal-backdrop" onClick={onClose}>
      <div className="tf-modal" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function Shell({ children, footer, bodyRef, step, regType, meta, pub, onBack, onNavigate, onSignOut, isAdmin }) {
  return (
    <div className="tf-root" ref={bodyRef}>
      <HamburgerMenu onNavigate={onNavigate} onSignOut={onSignOut} isAdmin={isAdmin} />
      <div className="tf-header">
        <div className="tf-header-left">
          <img src={'/ovr-logo.png'} alt="OVR" className="tf-logo" style={{ cursor: 'pointer' }} onClick={() => onNavigate && onNavigate('HOME')} />
          <div className="tf-header-meta">
            {meta
              ? <><span className="tf-tourn-badge">{meta.name}</span><span className="tf-tourn-date">{meta.date}</span></>
              : <span className="tf-tourn-badge">TOURNAMENT SIGN-UP</span>
            }
          </div>
        </div>
      </div>
      <StepBar step={step} regType={regType} />
      {/* keyed by step so each step's entrance animation replays */}
      <div className="tf-body" key={step}>{children}</div>
      {footer && <div className="tf-footer-actions">{footer}</div>}
    </div>
  );
}

export default function TournamentFlow({ onBack, isMobile, pub, restoreDraft, onDraftRestored, onCartAdded, onNavigate, onSignOut, isAdmin, userAvatar }) {
  const { user }          = useAuth();
  const { openAuthModal } = useAuthModal();
  const { addItem }       = useCart();
  const bodyRef = useRef(null);

  // Parse draft once - useState initializers only run on the first render,
  // so this gives the component the correct state immediately (no flash to 'tourn')
  const _d = (() => {
    if (!restoreDraft) return null;
    try {
      const d = JSON.parse(localStorage.getItem('ovrRegDraft') || 'null');
      if (d?.tournKey && !TOURN_META[d.tournKey]) {
        localStorage.removeItem('ovrRegDraft');
        return null;
      }
      return d;
    } catch (e) { return null; }
  })();

  const [step,     setStep]     = useState(_d?.step && _d.step !== 'done' ? _d.step : 'tourn');
  const [regType,  setRegType]  = useState(_d?.regType ?? null);
  const [tournKey, setTournKey] = useState(_d?.tournKey ?? null);

  const [team, setTeam] = useState(_d?.team ?? {
    name: '', captainFirstName: '', captainLastName: '',
    captainPhone: '', captainEmail: '',
    players: [emptyPlayer()],
  });
  const [fa, setFa] = useState(_d?.fa ?? {
    firstName: '', lastName: '', email: '', phone: '', gender: '', position: '',
  });
  const [hoveredRole, setHoveredRole] = useState(null);
  const [claimTick,   setClaimTick]   = useState(0);
  const [mediaRelease,   setMediaRelease]   = useState(_d?.mediaRelease ?? false);
  const [paymentConfirm, setPaymentConfirm] = useState(_d?.paymentConfirm ?? false);
  const [showBlitzInfo,  setShowBlitzInfo]  = useState(false);
  const [cupInfoKey,     setCupInfoKey]     = useState(null);
  const [rosterIssues,     setRosterIssues]     = useState([]);
  const [showRosterWarning, setShowRosterWarning] = useState(false);
  const [rowNameErrors,    setRowNameErrors]    = useState([]);
  const [faPool,           setFaPool]           = useState([]);
  const [showFaPicker,     setShowFaPicker]     = useState(false);
  const [formError,  setFormError]  = useState('');
  const [dbSeasons,  setDbSeasons]  = useState({});
  const [seasonsLoaded, setSeasonsLoaded] = useState(false);

  useEffect(() => {
    supabase.from('seasons').select('id, status, per_player_cents, fa_fee_cents, coming_soon, event_date, name, format, description')
      .neq('status', 'draft')
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(s => { map[s.id] = s; });
        setDbSeasons(map);
        setSeasonsLoaded(true);
      });
  }, []);

  // DB is source of truth; TOURN_META provides display fallbacks for legacy fields
  const getMeta = (key) => {
    const db = dbSeasons[key];
    const fallback = TOURN_META[key] || {};
    const perPlayerCents = db?.per_player_cents ?? fallback.perPlayerCents ?? 0;
    const faFeeCents = db?.fa_fee_cents ?? fallback.perPlayerCents ?? 0;
    return {
      ...fallback,
      name:   db?.name        || fallback.name   || key.toUpperCase(),
      format: db?.format      || fallback.format  || '',
      desc:   db?.description || fallback.desc    || '',
      perPlayerCents,
      faFee: `$${(faFeeCents / 100).toFixed(0)}`,
      fee: `$${(perPlayerCents / 100).toFixed(0)}/player`,
      isOpen: db?.status === 'open',
      comingSoon: db?.coming_soon ?? true,
    };
  };

  const meta = tournKey ? getMeta(tournKey) : null;

  // Tournaments shown on the pick step. "End tournament" in admin sets coming_soon
  // too, so completed seasons are excluded explicitly.
  const listedSeasons = Object.keys(dbSeasons)
    .sort((a, b) => a === 'blitz' ? -1 : b === 'blitz' ? 1 : a.localeCompare(b))
    .filter(key => { const m = getMeta(key); return m.isOpen || (m.comingSoon && dbSeasons[key]?.status !== 'completed'); });

  useEffect(() => {
    if (!user) return;
    const name  = user.user_metadata?.full_name || '';
    const email = user.email || '';
    setTeam(t => ({
      ...t,
      captainFirstName: t.captainFirstName || name.split(' ')[0] || '',
      captainLastName:  t.captainLastName  || name.split(' ').slice(1).join(' ') || '',
      captainEmail:     t.captainEmail     || email,
    }));
    setFa(f => ({
      ...f,
      firstName: f.firstName || name.split(' ')[0] || '',
      lastName:  f.lastName  || name.split(' ').slice(1).join(' ') || '',
      email:     f.email     || email,
    }));
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (step !== 'roster') return;
    setTeam(t => {
      const p0 = t.players[0] || emptyPlayer();
      if (!p0.firstName && t.captainFirstName) {
        const next = [...t.players];
        next[0] = { ...p0, firstName: t.captainFirstName, lastName: t.captainLastName };
        return { ...t, players: next };
      }
      return t;
    });
  }, [step]); // eslint-disable-line

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (step !== 'roster' || !tournKey) return;
    let active = true;
    (async () => {
      const { data: fas } = await supabase
        .from('tournament_free_agents')
        .select('*')
        .eq('tournament', tournKey)
        .eq('payment_status', 'paid')
        .is('assigned_team_id', null)
        .order('position');
      if (!active) return;
      if (!fas || fas.length === 0) { setFaPool([]); return; }
      const ids = [...new Set(fas.map(f => f.user_id).filter(Boolean))];
      const { data: profiles } = ids.length
        ? await supabase.from('profiles').select('id, avatar_url').in('id', ids)
        : { data: [] };
      if (!active) return;
      const avatarMap = {};
      (profiles || []).forEach(p => { avatarMap[p.id] = p.avatar_url; });
      setFaPool(fas.map(fa => ({ ...fa, avatar_url: avatarMap[fa.user_id] || null })));
    })();
    return () => { active = false; };
  }, [step, tournKey]); // eslint-disable-line

  // Save in-progress registration to localStorage so it survives sign-out
  useEffect(() => {
    if (step === 'done') { localStorage.removeItem('ovrRegDraft'); return; }
    if (!tournKey || step === 'tourn') return;
    localStorage.setItem('ovrRegDraft', JSON.stringify({
      step, tournKey, regType, team, fa, mediaRelease, paymentConfirm, savedAt: Date.now(),
    }));
  }, [step, tournKey, regType, team, fa, mediaRelease, paymentConfirm]); // eslint-disable-line

  // State is already initialized from draft in useState above.
  // This effect just clears the restoreDraft flag in the parent.
  useEffect(() => {
    if (restoreDraft) onDraftRestored?.();
  }, [restoreDraft]); // eslint-disable-line

  const goTo = s => { setFormError(''); setStep(s); };

  const sp = { bodyRef, step, regType, meta, pub, onBack, onNavigate, onSignOut, isAdmin };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_RE = /^[2-9]\d{9}$/; // NANP: leading digit 2-9, 10 digits total

  const handleTeamInfoSubmit = e => {
    e.preventDefault();
    setFormError('');
    const { name, captainFirstName, captainLastName, captainPhone, captainEmail } = team;
    if (!name.trim())             { setFormError('Please enter a team name.'); return; }
    if (!captainFirstName.trim()) { setFormError("Enter the captain's first name."); return; }
    if (!captainLastName.trim())  { setFormError("Enter the captain's last name."); return; }
    if (!captainEmail.trim() || !EMAIL_RE.test(captainEmail.trim())) {
      setFormError("Enter a valid email address."); return;
    }
    const digits = captainPhone.replace(/\D/g, '');
    if (!PHONE_RE.test(digits)) {
      setFormError('Enter a valid 10-digit North American phone number.'); return;
    }
    goTo('roster');
  };

  const handleRosterContinue = () => {
    setFormError('');
    // Every row must have first name, last name, and gender
    const errs = team.players.map(p => !p.firstName.trim() || !p.lastName.trim() || !p.gender);
    if (errs.some(Boolean)) {
      setRowNameErrors(errs);
      setFormError('Every player needs a first name, last name, and gender selected.');
      return;
    }
    setRowNameErrors([]);
    // Duplicate name check
    const names = team.players.map(p => `${p.firstName.trim()} ${p.lastName.trim()}`.toLowerCase());
    const hasDups = names.some((n, i) => n && names.indexOf(n) !== i);
    if (hasDups) { setFormError('Two players have the same name. Please check the roster.'); return; }
    const count = namedCount(team.players);
    const women = womenCount(team.players);
    if (count < 1) { setFormError('Add at least one player.'); return; }
    const issues = [];
    if (count < MIN_ROSTER) issues.push('under_min');
    if (women < MIN_WOMEN)  issues.push('coed');
    if (issues.length > 0) { setRosterIssues(issues); setShowRosterWarning(true); return; }
    goTo('confirm');
  };

  const handleFaInfoSubmit = e => {
    e.preventDefault();
    setFormError('');
    if (!fa.firstName.trim()) { setFormError('Enter your first name.'); return; }
    if (!fa.lastName.trim())  { setFormError('Enter your last name.'); return; }
    if (!fa.email.trim() || !EMAIL_RE.test(fa.email.trim())) {
      setFormError('Enter a valid email address.'); return;
    }
    const faDigits = fa.phone.replace(/\D/g, '');
    if (!PHONE_RE.test(faDigits)) {
      setFormError('Enter a valid 10-digit North American phone number.'); return;
    }
    if (!fa.gender) { setFormError('Select your gender.'); return; }
    goTo('fa_pos');
  };

  const updatePlayer = (idx, field, val) =>
    setTeam(t => ({ ...t, players: t.players.map((p, i) => i === idx ? { ...p, [field]: val } : p) }));
  const addPlayer = () =>
    setTeam(t => t.players.length < MAX_ROSTER ? { ...t, players: [...t.players, emptyPlayer()] } : t);

  const refreshFaPool = async () => {
    if (!tournKey) return;
    const { data: fas } = await supabase
      .from('tournament_free_agents')
      .select('*')
      .eq('tournament', tournKey)
      .eq('payment_status', 'paid')
      .is('assigned_team_id', null)
      .order('position');
    if (!fas || fas.length === 0) { setFaPool([]); return; }
    const ids = [...new Set(fas.map(f => f.user_id).filter(Boolean))];
    const { data: profiles } = ids.length
      ? await supabase.from('profiles').select('id, avatar_url').in('id', ids)
      : { data: [] };
    const avatarMap = {};
    (profiles || []).forEach(p => { avatarMap[p.id] = p.avatar_url; });
    setFaPool(fas.map(fa => ({ ...fa, avatar_url: avatarMap[fa.user_id] || null })));
  };

  const addFaToRoster = (fa) => {
    const parts = (fa.name || '').split(' ');
    setTeam(t => {
      if (t.players.length >= MAX_ROSTER) return t;
      return { ...t, players: [...t.players, {
        firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '',
        gender: fa.gender, position: fa.position, fa_id: fa.id,
      }]};
    });
    setShowFaPicker(false);
  };
  const removePlayer = idx =>
    setTeam(t => ({ ...t, players: t.players.filter((_, i) => i !== idx) }));

  /* ── TOURNAMENT SELECTION ── */
  if (step === 'tourn') return (
    <Shell {...sp}>
      <BlitzInfoModal open={showBlitzInfo} onClose={() => setShowBlitzInfo(false)} />
      <OvrCupInfoModal open={!!cupInfoKey} event={CUP_INFO[cupInfoKey]} onClose={() => setCupInfoKey(null)} />
      <div className="tf-eyebrow">Step 1 · Select Event</div>
      <h1 className="tf-title">PICK YOUR<br /><em>TOURNAMENT</em></h1>
      <p className="tf-sub">Choose the event you're registering for. Each format has different pricing, schedule, and structure.</p>
      {seasonsLoaded && listedSeasons.length === 0 && (
        <div style={{ border: '1px solid #222', borderLeft: '3px solid #9D1515', padding: '22px 24px', maxWidth: 560 }}>
          <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 26, letterSpacing: '0.06em', color: '#fff', marginBottom: 6 }}>NO TOURNAMENTS OPEN RIGHT NOW</div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
            Registration for the next event hasn't opened yet. Check Upcoming Events or follow <a href="https://www.instagram.com/ovrleague" target="_blank" rel="noreferrer" style={{ color: '#fff' }}>@ovrleague</a> for the announcement.
          </div>
        </div>
      )}
      <div className="tf-type-grid">
        {listedSeasons
          .map((key, i) => {
            const m = getMeta(key);
            const soldOut = !!SOLD_OUT[key];
            const blocked = !m.isOpen || soldOut;
            const ebActive = isEarlyBirdActive(key);
            const eb = EARLY_BIRD[key];
            const fullTeamPrice = (m.perPlayerCents || 0) * MIN_ROSTER / 100;
            const discountedTeamPrice = ebActive && eb ? fullTeamPrice - eb.discountCents / 100 : fullTeamPrice;
            return (
              <button
                key={key}
                className="tf-type-card"
                disabled={blocked}
                onClick={() => { if (!blocked) { setTournKey(key); goTo('type'); } }}
                style={blocked ? { position: 'relative', cursor: 'not-allowed' } : undefined}
              >
                {blocked && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none', border: '2px solid #9D1515' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 42, letterSpacing: '0.16em', color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>{soldOut ? 'SOLD OUT' : 'COMING SOON'}</div>
                      <div style={{ fontSize: 11, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginTop: 8 }}>{soldOut ? 'Registration closed' : 'Registration opens soon'}</div>
                    </div>
                  </div>
                )}
                {key === 'blitz'
                  ? <div className="tf-type-price">{m.fee}</div>
                  : <div className="tf-type-price">
                      {ebActive
                        ? <><span style={{ textDecoration: 'line-through', opacity: 0.4, marginRight: 6 }}>${fullTeamPrice.toFixed(0)}</span>${discountedTeamPrice.toFixed(0)}</>
                        : `$${fullTeamPrice.toFixed(0)}`
                      }
                      <span style={{fontSize:11,fontFamily:'Manrope,sans-serif',letterSpacing:'0.06em'}}>/team of {MIN_ROSTER}</span>
                    </div>
                }
                {ebActive && key !== 'blitz' && (
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.13em', color: '#d8a64a', textTransform: 'uppercase', marginBottom: 2, maxWidth: 'calc(100% - 210px)' }}>
                    EARLY BIRD DISCOUNT · ENDS JUNE 18 AT 12AM
                  </div>
                )}
                <div className="tf-type-num">FORMAT {String(i + 1).padStart(2, '0')}</div>
                <div className="tf-type-label">{m.name}</div>
                {m.desc && <p className="tf-type-desc">{m.desc}</p>}
                <ul className="tf-type-list">
                  {m.format && <li>{m.format}</li>}
                  <li>${(m.perPlayerCents / 100).toFixed(0)}/player · extras at half price (7th &amp; 8th)</li>
                  <li>Min {MIN_ROSTER}, max {MAX_ROSTER} players per team</li>
                  <li>Co-ed: min {MIN_WOMEN} women on court at all times</li>
                  <li>Free agents: {m.faFee} flat</li>
                </ul>
                {key === 'blitz' && (
                  <div
                    role="button" tabIndex={0}
                    onClick={e => { e.stopPropagation(); setShowBlitzInfo(true); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); setShowBlitzInfo(true); } }}
                    style={{ marginTop: 14, display: 'inline-block', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.18em', color: '#9D1515', borderBottom: '1px solid #9D1515', padding: '4px 0', cursor: 'pointer' }}
                  >
                    LEARN MORE ABOUT BLITZ →
                  </div>
                )}
                {CUP_INFO[key] && (
                  <div
                    role="button" tabIndex={0}
                    onClick={e => { e.stopPropagation(); setCupInfoKey(key); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); setCupInfoKey(key); } }}
                    style={{ marginTop: 14, display: 'inline-block', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.18em', color: '#9D1515', borderBottom: '1px solid #9D1515', padding: '4px 0', cursor: 'pointer' }}
                  >
                    HOW THE CUP WORKS →
                  </div>
                )}
              </button>
            );
          })}
      </div>
    </Shell>
  );

  /* ── TYPE SELECTION ── */
  if (step === 'type') {
    const ebActiveType = isEarlyBirdActive(tournKey);
    const ebType = EARLY_BIRD[tournKey];
    const fullTeamPriceType = (meta?.perPlayerCents || 0) * MIN_ROSTER / 100;
    const discountedTeamPriceType = ebActiveType && ebType ? fullTeamPriceType - ebType.discountCents / 100 : fullTeamPriceType;
    return (
    <Shell {...sp} footer={
      <div className="tf-footer-left">
        <button className="tf-btn-ghost" onClick={() => goTo('tourn')}>← BACK</button>
      </div>
    }>
      <div className="tf-eyebrow">{meta?.name} · How are you signing up?</div>
      <h1 className="tf-title">TEAM OR<br /><em>FREE AGENT?</em></h1>
      <p className="tf-sub">Bring your own roster, or sign up solo and we'll match you with a team that needs your position.</p>
      <div className="tf-type-grid">

        <button className="tf-type-card tf-type-team" onClick={() => { setRegType('team'); goTo('team_info'); }}>
          <div className="tf-type-price">
            {ebActiveType
              ? <><span style={{ textDecoration: 'line-through', opacity: 0.4, marginRight: 6 }}>${fullTeamPriceType.toFixed(0)}</span>${discountedTeamPriceType.toFixed(0)}</>
              : `$${fullTeamPriceType.toFixed(0)}`
            }
            <span style={{fontSize:11,fontFamily:'Manrope,sans-serif',letterSpacing:'0.06em'}}>/team of {MIN_ROSTER}</span>
          </div>
          {ebActiveType && (
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.13em', color: '#d8a64a', textTransform: 'uppercase', marginBottom: 2, maxWidth: 'calc(100% - 210px)' }}>
              EARLY BIRD DISCOUNT · ENDS JUNE 18 AT 12AM
            </div>
          )}
          <div className="tf-type-num">OPTION 01</div>
          <div className="tf-type-label">FULL TEAM</div>
          <p className="tf-type-desc">You're the captain. Bring your own roster. Min {MIN_ROSTER} players, max {MAX_ROSTER}.</p>
          <ul className="tf-type-list">
            <li>{meta?.fee} · price scales with roster size</li>
            <li>You manage the roster</li>
            <li>Under {MIN_ROSTER}? Browse free agents to fill spots</li>
            <li>Co-ed rule: min {MIN_WOMEN} women on court at all times</li>
          </ul>
        </button>

        <button className="tf-type-card tf-type-fa" onClick={() => { setRegType('fa'); goTo('fa_info'); }}>
          <div className="tf-type-price">{meta?.faFee}<span style={{fontSize:11,fontFamily:'Manrope,sans-serif',letterSpacing:'0.06em'}}>/player</span></div>
          <div className="tf-type-num">OPTION 02</div>
          <div className="tf-type-label">FREE AGENT</div>
          <p className="tf-type-desc">No team? Pick your position and we'll connect you with a team that needs you.</p>
          <ul className="tf-type-list">
            <li>Visible to captains looking to fill rosters</li>
            <li>Pick your position so you land in the right spot</li>
            <li>OVR facilitates the connection</li>
          </ul>
        </button>

      </div>
    </Shell>
    );
  }

  /* ── TEAM INFO ── */
  if (step === 'team_info') return (
    <Shell {...sp} footer={
      <>
        <div className="tf-footer-left">
          <button className="tf-btn-ghost" onClick={() => goTo('type')}>← BACK</button>
        </div>
        <div className="tf-footer-right">
          <button className="tf-btn-primary" type="submit" form="tf-team-info-form">
            NEXT →
          </button>
        </div>
      </>
    }>
      <div className="tf-eyebrow">{meta?.name} · Team Registration</div>
      <h1 className="tf-title">YOUR<br /><em>TEAM INFO</em></h1>
      <p className="tf-sub">We only need full contact info for the captain. They handle payment and roster changes.</p>
      <form id="tf-team-info-form" className="tf-form" onSubmit={handleTeamInfoSubmit}>
        <div className="tf-form-grid">
          <div className="tf-field tf-field-full">
            <label className="tf-label">TEAM NAME</label>
            <input
              className="tf-input"
              value={team.name}
              onChange={e => setTeam(t => ({ ...t, name: e.target.value }))}
              placeholder="e.g. Six Pack Setters"
              autoComplete="off"
            />
          </div>
          <div className="tf-field-section">CAPTAIN CONTACT</div>
          <div className="tf-field">
            <label className="tf-label">FIRST NAME</label>
            <input
              className="tf-input"
              value={team.captainFirstName}
              onChange={e => setTeam(t => ({ ...t, captainFirstName: e.target.value }))}
              placeholder="First name"
              autoComplete="given-name"
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">LAST NAME</label>
            <input
              className="tf-input"
              value={team.captainLastName}
              onChange={e => setTeam(t => ({ ...t, captainLastName: e.target.value }))}
              placeholder="Last name"
              autoComplete="family-name"
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">PHONE</label>
            <input
              className="tf-input"
              type="tel"
              inputMode="numeric"
              value={team.captainPhone}
              onChange={e => setTeam(t => ({ ...t, captainPhone: e.target.value }))}
              placeholder="613 555 0100"
              autoComplete="tel"
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">EMAIL</label>
            <input
              className="tf-input"
              type="email"
              value={team.captainEmail}
              onChange={e => setTeam(t => ({ ...t, captainEmail: e.target.value }))}
              placeholder="captain@email.com"
              autoComplete="email"
            />
          </div>
        </div>
        {formError && <div className="tf-form-error">{formError}</div>}
      </form>
    </Shell>
  );

  /* ── ROSTER ── */
  if (step === 'roster') {
    const count  = namedCount(team.players);
    const women  = womenCount(team.players);
    const pct    = Math.min(1, count / MIN_ROSTER);
    const sizeOk = count >= MIN_ROSTER;
    const coedOk = women >= MIN_WOMEN;
    return (
      <Shell {...sp} footer={
        <>
          <div className="tf-footer-left">
            <button className="tf-btn-ghost" onClick={() => goTo('team_info')}>← BACK</button>
            <span className="tf-footer-summary">
              <b>{count}</b> of {MIN_ROSTER} min · <b>{women}</b> women
            </span>
          </div>
          <div className="tf-footer-right">
            <button className="tf-btn-primary" onClick={handleRosterContinue}>
              NEXT →
            </button>
          </div>
        </>
      }>
        {showRosterWarning && (
          <Modal onClose={() => setShowRosterWarning(false)}>
            <div className="tf-modal-eyebrow">
              Roster {rosterIssues.length > 1 ? '2 issues' : '1 issue'} · {meta?.name}
            </div>
            <h2 className="tf-modal-title">
              YOUR ROSTER <em>NEEDS ATTENTION</em>
            </h2>
            {rosterIssues.includes('under_min') && (
              <p className="tf-modal-body">
                <strong style={{ color: '#fff' }}>Roster too small:</strong> You have <strong>{count}</strong> of <strong>{MIN_ROSTER}</strong> minimum players. Need {MIN_ROSTER - count} more - browse the free agent pool to fill your spots.
              </p>
            )}
            {rosterIssues.includes('coed') && (
              <p className="tf-modal-body">
                <strong style={{ color: '#fff' }}>Co-ed rule:</strong> Need at least <strong>{MIN_WOMEN} women</strong> on court. You currently have <strong>{women}</strong>.
              </p>
            )}
            <div className="tf-modal-actions">
              <button className="tf-btn-ghost" onClick={() => setShowRosterWarning(false)}>
                ← FIX ROSTER
              </button>
              {rosterIssues.includes('under_min') && (
                <button className="tf-btn-primary" onClick={() => { setShowRosterWarning(false); refreshFaPool(); setShowFaPicker(true); }}>
                  BROWSE FREE AGENTS →
                </button>
              )}
            </div>
          </Modal>
        )}

        {showFaPicker && (() => {
          const available = faPool.filter(fa => !team.players.some(p => p.fa_id === fa.id));
          const currentWomen = womenCount(team.players);
          const needsWomen = currentWomen < MIN_WOMEN;
          const womenShort = Math.max(0, MIN_WOMEN - currentWomen);
          // Female FAs first when co-ed rule not met, then alphabetical within each group
          const sorted = needsWomen
            ? [...available].sort((a, b) => {
                if (a.gender === 'F' && b.gender !== 'F') return -1;
                if (b.gender === 'F' && a.gender !== 'F') return 1;
                return 0;
              })
            : available;
          const FaRow = ({ fa }) => {
            const isF = fa.gender === 'F';
            const suggested = needsWomen && isF;
            return (
              <button key={fa.id} className={`tf-pos-row ${suggested ? 'highlighted' : ''}`}
                onClick={() => addFaToRoster(fa)}
                style={{ position: 'relative' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: 'rgba(157,21,21,0.15)', border: '1px solid rgba(157,21,21,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {fa.avatar_url
                    ? <img src={fa.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(157,21,21,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  }
                </div>
                <span>
                  <span className="tf-pos-name">{fa.name}</span>
                  <span style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                    {fa.gender && (() => {
                      const gColor = fa.gender === 'M' ? '#58b6e7' : fa.gender === 'F' ? '#f0686b' : '#8a8a8a';
                      const gBorder = fa.gender === 'M' ? 'rgba(88,182,231,0.4)' : fa.gender === 'F' ? 'rgba(240,104,107,0.4)' : '#3a3a3a';
                      return <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:20, height:20, fontFamily:"'JetBrains Mono',monospace", fontSize:9, border:`1px solid ${gBorder}`, color:gColor, background:'#0e0e0e', flexShrink:0 }}>{fa.gender}</span>;
                    })()}
                    {fa.position && (() => {
                      const c = { S: ['#7c5cff','#b9a8ff'], OH: ['#d8a64a','#ecc77a'], MB: ['#6bd6a3','#94e7c0'], OPP: ['#f0686b','#ffa3a5'], L: ['#58b6e7','#8fd1f1'] }[fa.position] || ['#3a3a3a','#8a8a8a'];
                      return <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', fontFamily:"'Bebas Neue',sans-serif", fontSize:10, letterSpacing:'0.08em', padding:'1px 6px', border:`1px solid ${c[0]}`, color:c[1], background:'#0e0e0e' }}>{fa.position}</span>;
                    })()}
                  </span>
                </span>
                {suggested
                  ? <span style={{ fontSize: 10, color: '#6bd6a3', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>NEEDED ✓</span>
                  : <span style={{ fontSize: 11, color: '#9D1515', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.1em' }}>ADD +</span>
                }
              </button>
            );
          };
          return (
            <Modal onClose={() => setShowFaPicker(false)}>
              <div className="tf-modal-eyebrow">Free Agent Pool · {meta?.name}</div>
              <h2 className="tf-modal-title">ADD A <em>FREE AGENT</em></h2>
              {needsWomen && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(107,214,163,0.07)', border: '1px solid rgba(107,214,163,0.25)', padding: '10px 14px', marginBottom: 14 }}>
                  <span style={{ fontSize: 18 }}>⚠</span>
                  <span style={{ fontSize: 13, color: '#94e7c0' }}>
                    Co-ed rule: you still need <strong>{womenShort} more female player{womenShort !== 1 ? 's' : ''}</strong>. Suggested picks are highlighted below.
                  </span>
                </div>
              )}
              {available.length === 0 ? (
                <p className="tf-modal-body">No free agents have signed up for {meta?.name} yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto', marginTop: 4 }}>
                  {sorted.map(fa => <FaRow key={fa.id} fa={fa} />)}
                </div>
              )}
              <div className="tf-modal-actions">
                <button className="tf-btn-ghost" onClick={() => setShowFaPicker(false)}>CLOSE</button>
              </div>
            </Modal>
          );
        })()}

        <div className="tf-eyebrow">{meta?.name} · {team.name || 'Your Team'}</div>
        <h1 className="tf-title">BUILD YOUR<br /><em>ROSTER</em></h1>
        <p className="tf-sub">Min {MIN_ROSTER}, max {MAX_ROSTER}. Co-ed: at least <strong style={{color:'#fff'}}>{MIN_WOMEN} women</strong> on court at all times.</p>

        <div className="tf-roster-wrap">
          <div className="tf-roster-left">
            <div className="tf-roster-list">
              {team.players.map((p, i) => (
                <div key={i} className={`tf-player-row ${i === 0 ? 'captain' : p.fa_id ? 'fa-player' : ''}`}>
                  <div className="tf-prow-num">{String(i + 1).padStart(2, '0')}</div>
                  {p.fa_id ? (
                    <>
                      <span className="tf-prow-input tf-prow-input--locked">{p.firstName}</span>
                      <span className="tf-prow-input tf-prow-input--locked">{p.lastName}</span>
                      <div className="tf-prow-gender">
                        <button type="button" className={`tf-gender-mini ${p.gender === 'M' ? 'active' : ''}`} disabled style={{ opacity: 0.4 }}>M</button>
                        <button type="button" className={`tf-gender-mini ${p.gender === 'F' ? 'active' : ''}`} disabled style={{ opacity: 0.4 }}>F</button>
                      </div>
                      <select className="tf-prow-pos" value={p.position} disabled style={{ opacity: 0.4 }}>
                        <option value="">POS</option>
                        {ROLE_ORDER.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <input
                        className={`tf-prow-input${rowNameErrors[i] && !p.firstName.trim() ? ' error' : ''}`}
                        placeholder={i === 0 ? 'First (captain)' : 'First name'}
                        value={p.firstName}
                        onChange={e => { updatePlayer(i, 'firstName', e.target.value); setRowNameErrors([]); }}
                      />
                      <input
                        className={`tf-prow-input${rowNameErrors[i] && !p.lastName.trim() ? ' error' : ''}`}
                        placeholder="Last name"
                        value={p.lastName}
                        onChange={e => { updatePlayer(i, 'lastName', e.target.value); setRowNameErrors([]); }}
                      />
                      <div className={`tf-prow-gender${rowNameErrors[i] && !p.gender ? ' error' : ''}`}>
                        <button type="button"
                          className={`tf-gender-mini ${p.gender === 'M' ? 'active' : ''}`}
                          onClick={() => { updatePlayer(i, 'gender', p.gender === 'M' ? '' : 'M'); setRowNameErrors([]); }}>M</button>
                        <button type="button"
                          className={`tf-gender-mini ${p.gender === 'F' ? 'active' : ''}`}
                          onClick={() => { updatePlayer(i, 'gender', p.gender === 'F' ? '' : 'F'); setRowNameErrors([]); }}>F</button>
                      </div>
                      <select className="tf-prow-pos" value={p.position}
                        onChange={e => updatePlayer(i, 'position', e.target.value)}>
                        <option value="">POS</option>
                        {ROLE_ORDER.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </>
                  )}
                  {i === 0
                    ? <span className="tf-prow-tag">CAPT</span>
                    : p.fa_id
                      ? <div className="tf-prow-fa-cell">
                          <span className="tf-prow-tag tf-prow-tag--fa">FA</span>
                          <button type="button" className="tf-prow-remove" onClick={() => removePlayer(i)}>×</button>
                        </div>
                      : <button type="button" className="tf-prow-remove" onClick={() => removePlayer(i)}>×</button>
                  }
                </div>
              ))}
            </div>
            <button type="button" className="tf-add-player" onClick={addPlayer}
              disabled={team.players.length >= MAX_ROSTER}>
              + ADD PLAYER {team.players.length >= MAX_ROSTER
                ? `· MAX ${MAX_ROSTER}`
                : `(${team.players.length} / ${MAX_ROSTER})`}
            </button>
            <button type="button" className="tf-view-fa-btn tf-view-fa-btn--mobile" onClick={() => { refreshFaPool(); setShowFaPicker(true); }}>
              BROWSE FREE AGENTS →
            </button>
            {formError && <div className="tf-form-error">{formError}</div>}
          </div>

          <div className="tf-roster-rail">
            <button type="button" className="tf-view-fa-btn" onClick={() => { refreshFaPool(); setShowFaPicker(true); }}>
              BROWSE FREE AGENTS →
            </button>
            <div className="tf-stat-card">
              <div className="tf-stat-head">ROSTER</div>
              <div className={`tf-stat-big ${sizeOk ? '' : count > 0 ? 'warn' : ''}`}>
                {count}<small>/{MIN_ROSTER}</small>
              </div>
              <div className="tf-stat-bar">
                <div className="tf-stat-fill" style={{ transform: `scaleX(${pct})` }} />
              </div>
              <div className="tf-stat-note">
                {count < MIN_ROSTER
                  ? `NEED ${MIN_ROSTER - count} MORE`
                  : count < MAX_ROSTER
                    ? `VALID · ${MAX_ROSTER - count} SLOTS LEFT`
                    : `FULL · ${MAX_ROSTER}/${MAX_ROSTER}`}
              </div>
            </div>
            <div className="tf-stat-card">
              <div className="tf-stat-head">RULES</div>
              {[
                { label: 'Min players', val: MIN_ROSTER },
                { label: 'Max players', val: MAX_ROSTER },
                { label: 'Format',      val: '6s Co-ed' },
                { label: 'Min women',   val: `${women}/${MIN_WOMEN}`, ok: coedOk },
                { label: 'Captain',     val: fullName(team.players[0]) ? 'SET' : 'MISSING', ok: !!fullName(team.players[0]) },
              ].map(r => (
                <div key={r.label} className="tf-rule-row">
                  <span>{r.label}</span>
                  <strong className={r.ok === undefined ? '' : r.ok ? 'ok' : 'bad'}>{r.val}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  /* ── FA INFO ── */
  if (step === 'fa_info') return (
    <Shell {...sp} footer={
      <>
        <div className="tf-footer-left">
          <button className="tf-btn-ghost" onClick={() => goTo('type')}>← BACK</button>
        </div>
        <div className="tf-footer-right">
          <button className="tf-btn-primary" type="submit" form="tf-fa-info-form">
            NEXT →
          </button>
        </div>
      </>
    }>
      <div className="tf-eyebrow">{meta?.name} · Free Agent</div>
      <h1 className="tf-title">YOUR INFO</h1>
      <p className="tf-sub">Captains looking to fill spots will see your name and position. Be reachable - they may contact you the day before.</p>
      <form id="tf-fa-info-form" className="tf-form" onSubmit={handleFaInfoSubmit}>
        <div className="tf-form-grid">
          <div className="tf-field">
            <label className="tf-label">FIRST NAME</label>
            <input className="tf-input" value={fa.firstName}
              onChange={e => setFa(f => ({ ...f, firstName: e.target.value }))}
              placeholder="First name" autoComplete="given-name" />
          </div>
          <div className="tf-field">
            <label className="tf-label">LAST NAME</label>
            <input className="tf-input" value={fa.lastName}
              onChange={e => setFa(f => ({ ...f, lastName: e.target.value }))}
              placeholder="Last name" autoComplete="family-name" />
          </div>
          <div className="tf-field">
            <label className="tf-label">EMAIL</label>
            <input className="tf-input" type="email" value={fa.email}
              onChange={e => setFa(f => ({ ...f, email: e.target.value }))}
              placeholder="you@email.com" autoComplete="email" />
          </div>
          <div className="tf-field">
            <label className="tf-label">PHONE</label>
            <input className="tf-input" type="tel" inputMode="numeric" value={fa.phone}
              onChange={e => setFa(f => ({ ...f, phone: e.target.value }))}
              placeholder="613 555 0100" autoComplete="tel" />
          </div>
          <div className="tf-field tf-field-full">
            <label className="tf-label">
              GENDER <span className="tf-label-note">(for co-ed roster requirements)</span>
            </label>
            <div className="tf-seg">
              {[['M','MALE'],['F','FEMALE'],['NB','NON-BINARY']].map(([val, label]) => (
                <button key={val} type="button"
                  className={`tf-seg-btn ${fa.gender === val ? 'active' : ''}`}
                  onClick={() => setFa(f => ({ ...f, gender: val }))}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {formError && <div className="tf-form-error">{formError}</div>}
      </form>
    </Shell>
  );

  /* ── FA POSITION ── */
  if (step === 'fa_pos') return (
    <Shell {...sp} footer={
      <>
        <div className="tf-footer-left">
          <button className="tf-btn-ghost" onClick={() => goTo('fa_info')}>← BACK</button>
          {fa.position && (
            <span className="tf-footer-summary">
              Selected: <b>{ROLE_META[fa.position]?.name}</b>
            </span>
          )}
        </div>
        <div className="tf-footer-right">
          <button className="tf-btn-primary" disabled={!fa.position}
            onClick={() => fa.position && goTo('confirm')}>
            NEXT →
          </button>
        </div>
      </>
    }>
      <div className="tf-eyebrow">{meta?.name} · Where do you play?</div>
      <h1 className="tf-title">PICK YOUR<br /><em>POSITION</em></h1>
      <p className="tf-sub">Captains filter free agents by position. Pick the one you actually play - it's your best shot at getting placed.</p>

      <div className="tf-position-step">
        <div className="tf-court-frame">
          <div className="tf-court-toolbar">
            <h3>HALF COURT <span>· PREFERRED POSITION</span></h3>
          </div>
          <Court
            level="adv" rotation={0}
            claims={faClaims(fa.position)}
            hoveredRole={hoveredRole}
            onHoverRole={setHoveredRole}
            onClaim={role => { setFa(f => ({ ...f, position: role })); setClaimTick(t => t + 1); }}
            lastClaimedTick={claimTick}
            userAvatar={userAvatar}
          />
          <div className="tf-court-legend">
            <div className="item"><span className="swatch open" /> Open slot</div>
            <div className="item"><span className="swatch you" /> Your pick</div>
          </div>
        </div>

        <div className="tf-side-rail">
          <div className="tf-rail-card">
            <h4>POSITIONS</h4>
            <div className="tf-pos-list">
              {ROLE_ORDER.map(r => {
                const m = ROLE_META[r];
                const here = fa.position === r;
                return (
                  <button key={r}
                    className={`tf-pos-row ${hoveredRole === r || here ? 'highlighted' : ''}`}
                    onMouseEnter={() => setHoveredRole(r)}
                    onMouseLeave={() => setHoveredRole(null)}
                    onClick={() => { setFa(f => ({ ...f, position: r })); setClaimTick(t => t + 1); }}>
                    <span className={`tf-pos-badge pos-${r}`}>{m.abbr}</span>
                    <span>
                      <span className="tf-pos-name">{m.name}</span>
                      <span className="tf-pos-desc">{m.desc}</span>
                    </span>
                    {here && <span className="tf-pos-pick">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="tf-rail-card">
            <h4>YOUR PICK</h4>
            {!fa.position
              ? <div className="tf-pos-empty">Click a slot on the court or pick from the list</div>
              : <>
                  <div className="tf-pos-chosen">{ROLE_META[fa.position]?.name?.toUpperCase()}</div>
                  <div className="tf-pos-chosen-sub">{ROLE_META[fa.position]?.desc}</div>
                </>
            }
          </div>
        </div>
      </div>
    </Shell>
  );

  /* ── CONFIRM ── */
  if (step === 'confirm') {
    const namedPlayers = team.players.filter(p => fullName(p));
    // Guard: team confirm requires at least one named player (prevents crafted-draft bypass)
    if (regType === 'team' && namedPlayers.length === 0) {
      goTo('roster');
      return null;
    }
    // Guard: FA confirm requires a position selected
    if (regType === 'fa' && !fa.position) {
      goTo('fa_pos');
      return null;
    }
    const canSubmit = mediaRelease && paymentConfirm;

    const handleAddToCart = () => {
      if (SOLD_OUT[tournKey]) { setFormError('This tournament is sold out.'); return; }
      const info = getMeta(tournKey);
      const faPlayers = team.players.filter(p => p.fa_id).map(p => ({
        fa_id: p.fa_id, firstName: p.firstName, lastName: p.lastName, position: p.position,
      }));
      // Team total: first 6 players pay full price, players 7 and 8 pay half price.
      const per = info?.perPlayerCents || 0;
      const baseCount = Math.min(6, namedPlayers.length);
      const extras    = Math.max(0, namedPlayers.length - 6);
      const teamTotalCents = per * baseCount + Math.round(per / 2) * extras;
      const ebDiscount = isEarlyBirdActive(tournKey) ? (EARLY_BIRD[tournKey]?.discountCents || 0) : 0;
      const finalTotalCents = Math.max(0, teamTotalCents - ebDiscount);
      const teamFee = finalTotalCents > 0 ? `$${(finalTotalCents / 100).toFixed(2).replace(/\.00$/, '')}` : info?.fee;
      addItem({
        type:      regType === 'team' ? 'tournament_team' : 'tournament_fa',
        label:     info?.name || tournKey.toUpperCase(),
        sublabel:  regType === 'team'
          ? `Team: ${team.name} · ${namedPlayers.length} player${namedPlayers.length !== 1 ? 's' : ''}`
          : `${fa.firstName} ${fa.lastName} · ${fa.position}`,
        fee:       regType === 'team' ? teamFee : info?.faFee,
        tournKey,
        // Used by the cart to display the ambassador "equal share" discount.
        ...(regType === 'team' ? { perPlayerCents: info?.perPlayerCents || 0, numPlayers: namedPlayers.length } : {}),
        ...(regType === 'team' ? { team: { ...team }, faPlayers } : { fa: { ...fa } }),
      });
      localStorage.removeItem('ovrRegDraft');
      onCartAdded?.();
      onBack();
      if (!user) openAuthModal(null, 'signup', 'checkout');
    };

    return (
      <Shell {...sp} footer={
        <>
          <div className="tf-footer-left">
            <button className="tf-btn-ghost" onClick={() => goTo(regType === 'team' ? 'roster' : 'fa_pos')}>← BACK</button>
          </div>
          <div className="tf-footer-right">
            <button
              type="button"
              className="tf-btn-primary"
              onClick={handleAddToCart}
              disabled={!canSubmit}
              style={{ opacity: canSubmit ? 1 : 0.35 }}
            >
              ADD TO CART →
            </button>
          </div>
        </>
      }>
        <div className="tf-eyebrow">Almost done</div>
        <h1 className="tf-title">REVIEW &amp;<br /><em>LOCK IT IN</em></h1>
        <p className="tf-sub">
          Review your details below, then add to cart and pay securely via Stripe.
        </p>

        <form id="tf-confirm-form" className="tf-form" onSubmit={e => e.preventDefault()}>
          <div className="tf-summary-card">
            <div className="tf-summary-row">
              <span className="tf-summary-label">TOURNAMENT</span>
              <div className="tf-summary-val">
                {meta?.name}
                <small>{meta?.date} · {meta?.format}</small>
              </div>
            </div>
            <div className="tf-summary-row">
              <span className="tf-summary-label">SIGN-UP</span>
              <div className="tf-summary-val">
                {regType === 'team' ? 'FULL TEAM' : 'FREE AGENT'}
                {regType === 'team' ? (() => {
                  const ebActive = isEarlyBirdActive(tournKey);
                  return ebActive
                    ? <small><span style={{textDecoration:'line-through',opacity:0.5}}>{meta?.fee}</span> ${((meta?.perPlayerCents || 0) / 100).toFixed(0)}/player · <span style={{color:'#d8a64a'}}>$20 early bird applied</span></small>
                    : <small>{meta?.fee} team fee</small>;
                })() : <small>{meta?.faFee} per player</small>}
              </div>
            </div>
            {regType === 'team' ? (
              <>
                <div className="tf-summary-row">
                  <span className="tf-summary-label">TEAM</span>
                  <div className="tf-summary-val">
                    {team.name}
                    <small>Captain: {team.captainFirstName} {team.captainLastName} · {team.captainEmail}</small>
                  </div>
                </div>
                <div className="tf-summary-row" style={{ alignItems: 'flex-start' }}>
                  <span className="tf-summary-label">ROSTER ({namedPlayers.length})</span>
                  <div className="tf-roster-mini">
                    {namedPlayers.map((p, i) => (
                      <div key={i} className="tf-roster-mini-row">
                        <span className="tf-rm-num">{String(i + 1).padStart(2, '0')}</span>
                        <span className="tf-rm-name">{fullName(p)}{i === 0 ? ' · CAPT' : ''}</span>
                        {p.gender   && <span className="tf-rm-g">{p.gender}</span>}
                        {p.position && <span className="tf-rm-pos">{p.position}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="tf-summary-row">
                  <span className="tf-summary-label">PLAYER</span>
                  <div className="tf-summary-val">
                    {fa.firstName} {fa.lastName}
                    <small>{fa.email} · {fa.gender === 'M' ? 'Male' : fa.gender === 'F' ? 'Female' : 'Non-binary'}</small>
                  </div>
                </div>
                <div className="tf-summary-row">
                  <span className="tf-summary-label">POSITION</span>
                  <div className="tf-summary-val">
                    {fa.position ? ROLE_META[fa.position]?.name?.toUpperCase() : '-'}
                    <small>{meta?.name}</small>
                  </div>
                </div>
              </>
            )}
          </div>

          <label className={`tf-waiver ${mediaRelease ? 'checked' : ''}`}
            onClick={e => { e.preventDefault(); setMediaRelease(v => !v); }}>
            <input type="checkbox" checked={mediaRelease} readOnly />
            <span className="tf-waiver-box" />
            <span className="tf-waiver-copy">
              <span className="tf-waiver-label">MEDIA WAIVER · REQUIRED</span>
              <span className="tf-waiver-text">
                I consent to OVR capturing <strong>photos and videos</strong> during the tournament and using them on OVR's social channels. Captains, please confirm with your team.
              </span>
            </span>
          </label>

          <label className={`tf-waiver ${paymentConfirm ? 'checked' : ''}`}
            onClick={e => { e.preventDefault(); setPaymentConfirm(v => !v); }}>
            <input type="checkbox" checked={paymentConfirm} readOnly />
            <span className="tf-waiver-box" />
            <span className="tf-waiver-copy">
              <span className="tf-waiver-label">PAYMENT & CONFIRMATION · REQUIRED</span>
              <span className="tf-waiver-text">
                {regType === 'team'
                  ? (() => {
                      const ebActive = isEarlyBirdActive(tournKey);
                      const eb = EARLY_BIRD[tournKey];
                      const fullPrice = (meta?.perPlayerCents || 0) * MIN_ROSTER / 100;
                      const finalPrice = ebActive && eb ? fullPrice - eb.discountCents / 100 : fullPrice;
                      return <>I understand my team's spot is not confirmed until payment of <strong>${finalPrice.toFixed(0)}</strong> is completed via Stripe checkout. Spots are first-come, first-served once payment is received.</>;
                    })()
                  : <>I understand my spot is not confirmed until payment of <strong>{meta?.faFee}</strong> is completed via Stripe checkout. Spots are first-come, first-served once payment is received.</>
                }
              </span>
            </span>
          </label>

          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>
            <InfoIcon
              title="Cancellation Policy"
              body={regType === 'team'
                ? 'Team registrations must be cancelled at least 5 days before the tournament start time to receive a full refund (minus processing fees). After that window, no refunds are issued.'
                : 'Free agent registrations must be cancelled at least 2 days before the tournament start time to receive a full refund (minus processing fees). If you are not placed on a team in time, your full registration fee is automatically refunded.'
              }
            />
            <span>By continuing you accept our cancellation policy.</span>
          </div>

          {formError && <div className="tf-form-error">{formError}</div>}
        </form>
      </Shell>
    );
  }

  /* ── DONE (fallback - normally user is taken to cart via ADD TO CART) ── */
  if (step === 'done') { onBack(); return null; }

  return null;
}
